import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import {
  createTag,
  deleteTag,
  getTags,
  updateTag,
} from "../../reducers/CodePerTagsReducer";
import DataTable from "../../components/DataTable";
import { maxLengthMessage } from "../../utils/functions";
import { updateOrCreateUserSetting } from "../../reducers/UserSettingsReducer";

const schema = yup.object().shape({
  code: yup
    .string()
    .trim()
    .required("Code is required")
    .max(10, maxLengthMessage(10)),
  description: yup
    .string()
    .trim()
    .required("Description is required")
    .max(100, maxLengthMessage(100)),
  category: yup.string().trim().max(10, maxLengthMessage(10)).nullable(),
  flags: yup.string().trim().max(10, maxLengthMessage(10)).nullable(),
  code2: yup.string().trim().max(10, maxLengthMessage(10)).nullable(),
  description2: yup.string().trim().max(100, maxLengthMessage(100)).nullable(),
  code3: yup.string().trim().max(10, maxLengthMessage(10)).nullable(),
  description3: yup.string().trim().max(100, maxLengthMessage(100)).nullable(),
  code4: yup.string().trim().max(10, maxLengthMessage(10)).nullable(),
  description4: yup.string().trim().max(100, maxLengthMessage(100)).nullable(),
  isDefault: yup.boolean().nullable(),
  isActive: yup.boolean().nullable(),
});

const initialFormValues = {
  code: "",
  description: "",
  category: "",
  isDefault: false,
  isActive: false,
  flags: "",
  code2: "",
  description2: "",
  code3: "",
  description3: "",
  code4: "",
  description4: "",
};

const INITIAL_SORT_MODEL = [{ field: "code", sort: "asc" }];

const Tags = () => {
  const dispatch = useDispatch();
  const { tags, loading, total } = useSelector((state) => state.tags);
  const themeMode = useSelector((state) => state.theme.mode);
  const { userData, isAuthenticated } = useSelector((state) => state.user);
  const { userSettings } = useSelector((state) => state.userSettings);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortModel, setSortModel] = useState(INITIAL_SORT_MODEL);
  const [columnsOrder, setColumnsOrder] = useState([]);
  const [columnsSize, setColumnsSize] = useState([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const baseColumns = useMemo(
    () => [
      { field: "code", headerName: "Code" },
      { field: "description", headerName: "Description", width: 225 },
      { field: "category", headerName: "Category" },
      { field: "priority", headerName: "Priority" },
      { field: "isDefault", headerName: "Default", type: "boolean" },
      { field: "isActive", headerName: "Active", type: "boolean" },
      { field: "flags", headerName: "Flags" },
      { field: "code2", headerName: "Code 2" },
      { field: "description2", headerName: "Description 2" },
      { field: "code3", headerName: "Code 3" },
      { field: "description3", headerName: "Description 3" },
      { field: "code4", headerName: "Code 4" },
      { field: "description4", headerName: "Description 4" },
    ],
    []
  );

  useEffect(() => {
    if (settingsLoaded) return;
    const settingsMap = Object.fromEntries(
      userSettings.map((s) => [s.key, s.value])
    );
    if (settingsMap["tagsTableColumnOrder"]) {
      setColumnsOrder(JSON.parse(settingsMap["tagsTableColumnOrder"]));
    }

    if (settingsMap["tagsTableColumnSizes"]) {
      const widthArray = JSON.parse(settingsMap["tagsTableColumnSizes"]);
      const result = widthArray.reduce((acc, curr) => {
        acc[curr.field] = curr.width;
        return acc;
      }, {});
      setColumnsSize(result);
    }

    if (settingsMap["tagsTableColumnVisibility"]) {
      setColumnVisibilityModel(
        JSON.parse(settingsMap["tagsTableColumnVisibility"])
      );
    }

    if (settingsMap["tagsTableSortModel"]) {
      const savedSortModel = JSON.parse(settingsMap["tagsTableSortModel"]);
      if (Array.isArray(savedSortModel)) {
        const validSortModel = savedSortModel.filter((model) =>
          baseColumns.some((col) => col.field === model.field)
        );
        if (validSortModel.length > 0) {
          setSortModel(savedSortModel);
        }
      }
    }
    setSettingsLoaded(true);
  }, [userSettings, settingsLoaded, baseColumns]);

  const columns = useMemo(() => {
    const hasOrder = columnsOrder.length > 0;
    const hasSizes = Object.keys(columnsSize).length > 0;

    if (!hasOrder && !hasSizes) return baseColumns;

    let baseOrderedColumns;

    if (hasOrder) {
      const ordered = columnsOrder
        .map((field) => baseColumns.find((col) => col.field === field))
        .filter(Boolean);

      const remaining = baseColumns.filter(
        (col) => !columnsOrder.includes(col.field)
      );

      const actionsIndex = ordered.findIndex((col) => col.field === "actions");
      const insertAt = actionsIndex > -1 ? actionsIndex : ordered.length;

      baseOrderedColumns = [
        ...ordered.slice(0, insertAt),
        ...remaining,
        ...ordered.slice(insertAt),
      ];
    } else {
      baseOrderedColumns = baseColumns;
    }

    const finalColumns = baseOrderedColumns.map((col) => ({
      ...col,
      width: columnsSize[col.field] ?? col.width ?? 100,
    }));

    return finalColumns;
  }, [baseColumns, columnsOrder, columnsSize]);

  const allColumnFields = baseColumns.map((col) => col.field);
  useEffect(() => {
    const defaultVisibility = {};
    allColumnFields.forEach((field) => {
      defaultVisibility[field] = true;
    });
    setColumnVisibilityModel((prev) => ({ ...defaultVisibility, ...prev }));
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !settingsLoaded) return;

    const sortField = sortModel[0]?.field || "code";
    const sortDirection = sortModel[0]?.sort || "asc";
    dispatch(getTags(page, pageSize, sortField, sortDirection));
  }, [dispatch, page, pageSize, sortModel, settingsLoaded, isAuthenticated]);

  const handleAdd = (data) => {
    dispatch(createTag(data));
  };

  const handleEdit = (data) => {
    dispatch(updateTag(data.code, data));
  };

  const handleDelete = (id) => {
    const userId = userData?.user?.ptsUserID;
    dispatch(deleteTag(id, userId));
  };

  const handleColumnOrderChange = (params, event, details) => {
    const newColumnOrder = details.api.getAllColumns().map((col) => col.field);
    if (userData?.user?.ptsUserID) {
      dispatch(
        updateOrCreateUserSetting(
          userData.user.ptsUserID,
          "tagsTableColumnOrder",
          JSON.stringify(newColumnOrder)
        )
      );
    }
  };

  const handleColumnWidthChange = (params, event, details) => {
    const columnSizes = details.api.getAllColumns().map((col) => {
      return {
        field: col.field,
        width: col.width,
      };
    });
    if (userData?.user?.ptsUserID) {
      dispatch(
        updateOrCreateUserSetting(
          userData.user.ptsUserID,
          "tagsTableColumnSizes",
          JSON.stringify(columnSizes)
        )
      );
    }
  };

  const handleSortModelChange = useCallback(
    (model) => {
      const newSortModel = model.length > 0 ? model : INITIAL_SORT_MODEL;

      // Avoid unnecessary state updates if sort model didn't change
      const isSortModelDifferent =
        JSON.stringify(newSortModel) !== JSON.stringify(sortModel);

      if (isSortModelDifferent) {
        setSortModel(newSortModel);
        setPage(0);

        if (userData?.user?.ptsUserID) {
          dispatch(
            updateOrCreateUserSetting(
              userData.user.ptsUserID,
              "tagsTableSortModel",
              JSON.stringify(newSortModel)
            )
          );
        }
      }
    },
    [dispatch, userData?.user?.ptsUserID, sortModel]
  );

  const handleColumnVisibilityModelChange = useCallback(
    (newModel) => {
      setColumnVisibilityModel(newModel);
      if (userData?.user?.ptsUserID) {
        dispatch(
          updateOrCreateUserSetting(
            userData.user.ptsUserID,
            "tagsTableColumnVisibility",
            JSON.stringify(newModel)
          )
        );
      }
    },
    [dispatch, userData?.user?.ptsUserID]
  );

  return (
    <DataTable
      data={tags}
      columns={columns}
      columnVisibilityModel={columnVisibilityModel}
      total={total}
      loading={loading}
      schema={schema}
      initialFormValues={initialFormValues}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      idField={"code"}
      themeMode={themeMode}
      page={page}
      pageSize={pageSize}
      sortModel={sortModel}
      setPage={setPage}
      setPageSize={setPageSize}
      onColumnOrderChange={handleColumnOrderChange}
      onColumnWidthChange={handleColumnWidthChange}
      onColumnVisibilityChange={handleColumnVisibilityModelChange}
      onSortModelChange={handleSortModelChange}
      title={"Tags"}
    />
  );
};

export default Tags;
